import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { Avatar, Checkbox, ConfirmDialog, EmptyState, Menu, Pill } from '@orion/ui';
import type { LeadResponse, ListLeadsQuery } from '@orion/contracts';
import { PageHeader } from '../../app/shell.js';
import { ListPagination } from '../../components/ListPagination.js';
import { useAuth } from '../../lib/auth.js';
import { useToast } from '../../lib/toast.js';
import { disambiguateNames } from '../../lib/disambiguate-names.js';
import { useAssignableMembersQuery } from '../me/api.js';
import { useCreateSavedView, useDeleteSavedView, useSavedViewsQuery } from '../saved-views/api.js';
import { useBulkAssignLeads, useBulkDeleteLeads, useLeadCountsQuery, useLeadSourceOptionsQuery, useLeadStatusOptionsQuery, useLeadsQuery } from './api.js';

const DENSITY_KEY = 'orion-leads-density';
type Density = 'comfortable' | 'compact';

type BuiltInTab = 'all' | 'mine' | 'hot' | 'followups' | 'unassigned';
/** A user's own saved view, selected alongside the 5 built-in tabs — its id
 * doubles as the tab key so both live in one `tab` state value. */
type Tab = BuiltInTab | string;

function endOfTodayIso(): string {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

/** docs/architecture screen "Leads — list". Every column, filter and
 * built-in tab is real data (apps/api/src/modules/crm/leads) — the design's
 * "Columns" customizer and per-lead "Change status"/"Add tag"/"Archive"
 * bulk actions aren't wired up: there's no column-visibility preference
 * store and no bulk tag/archive endpoint yet (archiving one lead already
 * doesn't exist as an action anywhere in this app, not just in bulk). Bulk
 * "Assign" is real — it's the existing per-lead `PATCH :id/assign`, called
 * once per selected row. */
export function LeadsListPage() {
  const { hasPermission, session } = useAuth();
  const search = useSearch({ from: '/_app/leads' }) as { view?: string };
  const [tab, setTab] = useState<Tab>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState('');
  const [statusId, setStatusId] = useState<string | undefined>();
  const [ownerId, setOwnerId] = useState<string | undefined>();
  const [sourceId, setSourceId] = useState<string | undefined>();
  // Set only from a saved view's own filters (`setTabFilter`) — the 5
  // built-in tabs below keep deriving these straight from `tab` itself,
  // same as before; a saved view needs its own slot since its id isn't
  // one of those fixed tab keys.
  const [viewTemperature, setViewTemperature] = useState<ListLeadsQuery['temperature']>();
  const [viewFollowUpToday, setViewFollowUpToday] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [density, setDensity] = useState<Density>(() => {
    try {
      return (localStorage.getItem(DENSITY_KEY) as Density) || 'comfortable';
    } catch {
      return 'comfortable';
    }
  });

  const { data: counts } = useLeadCountsQuery();
  const { data: statuses } = useLeadStatusOptionsQuery();
  const { data: sources } = useLeadSourceOptionsQuery();
  const { data: members } = useAssignableMembersQuery();
  const memberLabels = useMemo(() => disambiguateNames(members ?? []), [members]);
  const { data: savedViews } = useSavedViewsQuery('lead');
  const createView = useCreateSavedView('lead');
  const deleteView = useDeleteSavedView('lead');
  const bulkAssign = useBulkAssignLeads();
  const bulkDelete = useBulkDeleteLeads();
  const toast = useToast();
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [isSavingView, setIsSavingView] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const appliedViewFromUrl = useRef(false);

  const saveCurrentView = async () => {
    if (!newViewName.trim()) return;
    const created = await createView.mutateAsync({
      entityType: 'lead',
      name: newViewName.trim(),
      filters: { q },
      sort: [],
      columns: [],
      visibility: 'private',
      isDefault: false,
      showInSidebar: false,
    });
    setTab(created.id);
    setNewViewName('');
    setIsSavingView(false);
  };

  const { data, isLoading, isError } = useLeadsQuery({
    page,
    pageSize,
    q: q || undefined,
    statusId,
    ownerId,
    sourceId,
    temperature: tab === 'hot' ? 'hot' : viewTemperature,
    unassigned: tab === 'unassigned' ? true : undefined,
    nextFollowUpBefore: tab === 'followups' || viewFollowUpToday ? endOfTodayIso() : undefined,
  });
  const exportUrl = `/api/v1/leads/export${q ? `?q=${encodeURIComponent(q)}` : ''}`;

  const total = data?.meta?.total ?? 0;
  const leads = data?.data ?? [];
  const rowHeight = density === 'comfortable' ? 'h-12.5' : 'h-9';

  const setTabFilter = (t: Tab) => {
    setTab(t);
    setPage(1);
    const view = savedViews?.find((v) => v.id === t);
    const viewTemp = view?.filters.temperature;
    setViewTemperature(viewTemp === 'cold' || viewTemp === 'warm' || viewTemp === 'hot' ? viewTemp : undefined);
    setViewFollowUpToday(view?.filters.dueTodayOrOverdue === true);
    const viewQ = view?.filters.q;
    setQ(typeof viewQ === 'string' ? viewQ : '');
    const viewSourceId = view?.filters.sourceId;
    setSourceId(typeof viewSourceId === 'string' ? viewSourceId : undefined);
  };

  // `?view=<id>` is how the Sidebar's "Saved views" links (shell.tsx) point
  // here — applied once, the first time the matching view shows up in
  // `savedViews` (it may not have loaded yet on first render), not on every
  // re-render, or switching tabs afterward would keep snapping back to it.
  useEffect(() => {
    if (appliedViewFromUrl.current || !search.view || !savedViews) return;
    if (savedViews.some((v) => v.id === search.view)) {
      setTabFilter(search.view);
      appliedViewFromUrl.current = true;
    }
  }, [search.view, savedViews]);

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => (prev.size === leads.length ? new Set() : new Set(leads.map((l) => l.id))));
  };

  const runBulkDelete = async () => {
    setConfirmBulkDelete(false);
    const ids = [...selected];
    const { succeeded, failed } = await bulkDelete.mutateAsync(ids);
    setSelected(new Set());
    if (failed > 0) toast.error(`Deleted ${succeeded} of ${ids.length} leads — ${failed} failed.`);
    else toast.success(`Deleted ${succeeded} lead${succeeded === 1 ? '' : 's'}.`);
  };

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Leads' }, { label: 'All leads' }]} />
      <main className="flex flex-1 flex-col gap-4 overflow-hidden px-7 py-6">
        <div className="flex h-9 shrink-0 items-center justify-between">
          <div className="flex items-center gap-2.5">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em] text-ink">Leads</h1>
            <span className="flex h-5.5 items-center rounded-full bg-divider px-2 text-xs font-semibold tabular-nums text-text-secondary">{counts?.all ?? total}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/leads/import" className="flex h-9 items-center gap-2 rounded-control border border-border-strong bg-surface px-3.5 text-sm font-semibold text-text-secondary hover:bg-surface-subtle">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 16V4M7 9l5-5 5 5" />
                <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
              </svg>
              Import
            </Link>
            {hasPermission('crm.leads.export') && (
              <a href={exportUrl} className="flex h-9 items-center gap-2 rounded-control border border-border-strong bg-surface px-3.5 text-sm font-semibold text-text-secondary hover:bg-surface-subtle">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 4v12M7 11l5 5 5-5" />
                  <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
                </svg>
                Export
              </a>
            )}
            <Link to="/leads/new" className="flex h-9 items-center gap-2 rounded-control bg-primary px-3.5 text-sm font-semibold text-white hover:bg-primary-hover">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              New lead
            </Link>
          </div>
        </div>

        <nav aria-label="Saved views" className="flex h-10 shrink-0 items-end gap-6 border-b border-border">
          {(
            [
              ['all', 'All leads', counts?.all],
              ['mine', 'My leads', counts?.mine],
              ['hot', 'Hot leads', counts?.hot],
              ['followups', 'Follow-ups today', counts?.followUpsToday],
              ['unassigned', 'Unassigned', counts?.unassigned],
            ] as const
          ).map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTabFilter(key)}
              className={
                tab === key
                  ? '-mb-px flex h-10 items-center gap-2 border-b-2 border-primary text-sm font-semibold text-ink'
                  : 'flex h-10 items-center gap-2 text-sm font-medium text-text-muted hover:text-ink'
              }
            >
              {label}
              <span
                className={
                  tab === key
                    ? 'flex h-5 items-center rounded-full bg-primary-soft px-1.5 text-xs font-semibold text-primary-text'
                    : 'flex h-5 items-center rounded-full bg-divider px-1.5 text-xs font-semibold text-text-secondary'
                }
              >
                {count ?? '—'}
              </span>
            </button>
          ))}

          {savedViews
            ?.filter((view) => !view.isDefault)
            .map((view) => (
              <span
                key={view.id}
                className={
                  tab === view.id
                    ? '-mb-px flex h-10 items-center gap-1 border-b-2 border-primary text-sm font-semibold text-ink'
                    : 'flex h-10 items-center gap-1 text-sm font-medium text-text-muted hover:text-ink'
                }
              >
                <button type="button" onClick={() => setTabFilter(view.id)} className="flex h-10 items-center">
                  {view.name}
                </button>
                {view.ownerId === session?.membershipId && (
                  <button
                    type="button"
                    aria-label={`Delete view "${view.name}"`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (tab === view.id) setTabFilter('all');
                      deleteView.mutate(view.id);
                    }}
                    className="flex h-5 w-5 items-center justify-center rounded-full text-text-muted hover:bg-surface-subtle hover:text-danger"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                      <path d="M6 6l12 12M18 6 6 18" />
                    </svg>
                  </button>
                )}
              </span>
            ))}

          {isSavingView ? (
            <span className="flex items-center gap-1.5">
              <input
                autoFocus
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveCurrentView()}
                placeholder="View name"
                className="h-7 rounded-control border border-border-strong bg-surface px-2 text-xs"
              />
              <button type="button" onClick={saveCurrentView} className="text-xs font-semibold text-primary-text">
                Save
              </button>
              <button type="button" onClick={() => setIsSavingView(false)} className="text-xs text-text-muted">
                Cancel
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setIsSavingView(true)} className="flex h-10 items-center gap-1.5 text-[13px] font-semibold text-primary-text hover:underline">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2439B3" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Save view
            </button>
          )}
        </nav>

        <div className="flex h-8 shrink-0 items-center gap-2">
          <label className="flex h-8 w-58 items-center gap-2 rounded-control border border-border-strong bg-surface px-2.5">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <span className="sr-only">Search leads</span>
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              type="search"
              placeholder="Search name, company, email…"
              className="w-full bg-transparent text-sm text-ink placeholder:text-text-muted focus:outline-none"
            />
          </label>

          <Menu
            trigger={
              <button type="button" className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] text-text-secondary hover:bg-surface-subtle">
                Status: <span className="font-semibold text-ink">{statuses?.find((s) => s.id === statusId)?.name ?? 'Any'}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            }
            items={[
              { label: 'Any status', onSelect: () => setStatusId(undefined) },
              ...(statuses ?? []).map((s) => ({ label: s.name, onSelect: () => setStatusId(s.id) })),
            ]}
            align="left"
          />

          <Menu
            trigger={
              <button type="button" className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] text-text-secondary hover:bg-surface-subtle">
                Owner: <span className="font-semibold text-ink">{ownerId ? memberLabels.get(ownerId) : 'Anyone'}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            }
            items={[{ label: 'Anyone', onSelect: () => setOwnerId(undefined) }, ...(members ?? []).map((m) => ({ label: memberLabels.get(m.id) ?? m.displayName, onSelect: () => setOwnerId(m.id) }))]}
            align="left"
          />

          <Menu
            trigger={
              <button type="button" className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] text-text-secondary hover:bg-surface-subtle">
                Source: <span className="font-semibold text-ink">{sources?.find((s) => s.id === sourceId)?.name ?? 'Any'}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            }
            items={[{ label: 'Any source', onSelect: () => setSourceId(undefined) }, ...(sources ?? []).map((s) => ({ label: s.name, onSelect: () => setSourceId(s.id) }))]}
            align="left"
          />

          <div className="flex-1" />

          <div role="group" aria-label="Row density" className="flex h-8 items-center gap-0.5 rounded-control border border-border-strong bg-surface p-0.5">
            <button
              type="button"
              aria-label="Comfortable rows"
              aria-pressed={density === 'comfortable'}
              onClick={() => {
                setDensity('comfortable');
                try {
                  localStorage.setItem(DENSITY_KEY, 'comfortable');
                } catch {
                  // Per-device convenience only.
                }
              }}
              className={`flex size-7 items-center justify-center rounded-[6px] ${density === 'comfortable' ? 'bg-primary-soft text-primary-text' : 'text-text-muted hover:bg-surface-subtle'}`}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 7h16M4 17h16" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Compact rows"
              aria-pressed={density === 'compact'}
              onClick={() => {
                setDensity('compact');
                try {
                  localStorage.setItem(DENSITY_KEY, 'compact');
                } catch {
                  // Per-device convenience only.
                }
              }}
              className={`flex size-7 items-center justify-center rounded-[6px] ${density === 'compact' ? 'bg-primary-soft text-primary-text' : 'text-text-muted hover:bg-surface-subtle'}`}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 5h16M4 10h16M4 15h16M4 20h16" />
              </svg>
            </button>
          </div>
        </div>

        {isError && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            Something went wrong loading leads. Please try again.
          </div>
        )}

        {!isLoading && leads.length === 0 && (
          <EmptyState
            title="No leads yet"
            description="Capture your first lead and start building your pipeline."
            action={
              <Link to="/leads/new" className="inline-flex h-9 items-center rounded-control bg-primary px-3.5 text-sm font-semibold text-white">
                Create lead
              </Link>
            }
          />
        )}

        {leads.length > 0 && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-border bg-surface shadow-card">
            {selected.size > 0 && (
              <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-primary-soft px-4">
                <span className="mr-1 text-[13px] font-semibold text-primary-text">{selected.size} selected</span>
                <Menu
                  trigger={
                    <button type="button" className="flex h-7 items-center gap-1.5 rounded-control px-2.5 text-[13px] font-medium text-text-secondary hover:bg-surface">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                      </svg>
                      Assign
                    </button>
                  }
                  items={(members ?? []).map((m) => ({
                    label: memberLabels.get(m.id) ?? m.displayName,
                    onSelect: () => bulkAssign.mutate({ ids: [...selected], ownerId: m.id }),
                  }))}
                  align="left"
                />
                {hasPermission('crm.leads.delete') && (
                  <button
                    type="button"
                    onClick={() => setConfirmBulkDelete(true)}
                    className="flex h-7 items-center gap-1.5 rounded-control px-2.5 text-[13px] font-medium text-danger hover:bg-surface"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M4 7h16M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 7" />
                    </svg>
                    Delete
                  </button>
                )}
                <div className="flex-1" />
                <button type="button" onClick={toggleAll} className="text-[13px] font-semibold text-primary-text hover:underline">
                  Select all {total} leads
                </button>
                <button type="button" aria-label="Clear selection" onClick={() => setSelected(new Set())} className="text-primary-text hover:opacity-70">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              </div>
            )}

            <div role="table" aria-label="Leads" className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <div
                role="row"
                className="grid h-9 shrink-0 items-center gap-3 border-b border-border bg-ground px-4 text-xs font-semibold text-text-muted"
                style={{ gridTemplateColumns: '20px minmax(0,2fr) minmax(0,1.3fr) minmax(0,1.1fr) minmax(0,0.9fr) minmax(0,0.75fr) minmax(0,1.3fr) minmax(0,1fr) minmax(0,0.9fr) minmax(0,1.05fr) 24px' }}
              >
                <span className="flex justify-center">
                  <Checkbox aria-label="Select all leads on this page" checked={selected.size > 0 && selected.size === leads.length} onChange={toggleAll} hideLabel label="Select all" />
                </span>
                <span>Name</span>
                <span>Company</span>
                <span>Status</span>
                <span>Score</span>
                <span>Temp.</span>
                <span>Owner</span>
                <span>Source</span>
                <span>Last contact</span>
                <span>Next follow-up</span>
                <span className="sr-only">Actions</span>
              </div>

              {leads.map((lead) => (
                <LeadRow key={lead.id} lead={lead} density={density} rowHeight={rowHeight} selected={selected.has(lead.id)} onToggle={() => toggleRow(lead.id)} />
              ))}
            </div>
          </div>
        )}

        {total > 0 && (
          <ListPagination page={page} pageSize={pageSize} total={total} noun="leads" onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
        )}
      </main>

      <ConfirmDialog
        open={confirmBulkDelete}
        title={`Delete ${selected.size} lead${selected.size === 1 ? '' : 's'} permanently?`}
        description="This removes them from every list and search for good, along with their open and completed tasks. Notes and activity history stay on record, but there's no undo from here."
        confirmLabel="Delete"
        variant="danger"
        loading={bulkDelete.isPending}
        onConfirm={runBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        requireTypedConfirmation="DELETE"
      />
    </>
  );
}

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--color-success)';
  if (score >= 50) return 'var(--color-amber-deep)';
  return 'var(--color-danger)';
}

function TemperatureBadge({ temperature }: { temperature: LeadResponse['temperature'] }) {
  if (!temperature) return <span className="text-text-faint">—</span>;
  if (temperature === 'hot') {
    return (
      <span className="inline-flex h-5.5 items-center gap-1 rounded-[6px] bg-amber-soft px-2 text-xs font-semibold text-amber-text">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#C7781A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-2 1-3 1-3s1 2 2 2c0-3-1-5 1-8" />
        </svg>
        Hot
      </span>
    );
  }
  if (temperature === 'warm') {
    return <span className="inline-flex h-5.5 items-center rounded-[6px] bg-primary-soft px-2 text-xs font-semibold text-primary-text">Warm</span>;
  }
  return <span className="inline-flex h-5.5 items-center rounded-[6px] bg-divider px-2 text-xs font-semibold text-text-secondary">Cold</span>;
}

function LeadRow({
  lead,
  density,
  rowHeight,
  selected,
  onToggle,
}: {
  lead: LeadResponse;
  density: Density;
  rowHeight: string;
  selected: boolean;
  onToggle: () => void;
}) {
  const initials = `${lead.firstName[0] ?? ''}${lead.lastName[0] ?? ''}`.toUpperCase();
  const statusColor = lead.status.color;
  const isOverdueFollowUp = lead.nextFollowUpAt ? new Date(lead.nextFollowUpAt) < new Date() : false;

  return (
    <div
      role="row"
      aria-selected={selected}
      className={`grid shrink-0 items-center gap-3 border-b border-divider px-4 text-[13px] ${rowHeight} ${selected ? 'bg-primary-tint' : ''}`}
      style={{ gridTemplateColumns: '20px minmax(0,2fr) minmax(0,1.3fr) minmax(0,1.1fr) minmax(0,0.9fr) minmax(0,0.75fr) minmax(0,1.3fr) minmax(0,1fr) minmax(0,0.9fr) minmax(0,1.05fr) 24px' }}
    >
      <span className="flex justify-center">
        <Checkbox aria-label={`Select ${lead.fullName}`} checked={selected} onChange={onToggle} hideLabel label={`Select ${lead.fullName}`} />
      </span>
      <span className="flex min-w-0 items-center gap-2.5">
        <span
          className="flex size-7 shrink-0 items-center justify-center rounded-full text-[11px] font-[650]"
          style={{ backgroundColor: `${statusColor}1A`, color: statusColor }}
        >
          {initials}
        </span>
        <span className="flex min-w-0 flex-col">
          <Link to="/leads/$leadId" params={{ leadId: lead.id }} className="truncate text-sm font-semibold text-ink hover:underline">
            {lead.fullName}
          </Link>
          {density === 'comfortable' && <span className="truncate text-xs text-text-muted">{lead.email ?? '—'}</span>}
        </span>
      </span>
      <span className="truncate text-text-secondary">
        {lead.company ? (
          <Link to="/companies/$companyId" params={{ companyId: lead.company.id }} className="text-primary-soft-text hover:underline">
            {lead.company.name}
          </Link>
        ) : (
          (lead.companyName ?? '—')
        )}
      </span>
      <span>
        <Pill label={lead.status.name} color={lead.status.color} />
      </span>
      <span className="flex items-center gap-2 font-semibold tabular-nums">
        {lead.score !== null ? (
          <>
            <span className="flex h-1.5 w-10 rounded-full bg-divider">
              <span className="rounded-full" style={{ width: `${lead.score}%`, backgroundColor: scoreColor(lead.score) }} />
            </span>
            {lead.score}
          </>
        ) : (
          <span className="text-text-faint">—</span>
        )}
      </span>
      <span>
        <TemperatureBadge temperature={lead.temperature} />
      </span>
      <span className="flex min-w-0 items-center gap-2 text-text-secondary">
        {lead.owner ? (
          <>
            <Avatar name={lead.owner.displayName} size="sm" />
            <span className="truncate">{lead.owner.displayName}</span>
          </>
        ) : (
          <span className="text-text-faint">Unassigned</span>
        )}
      </span>
      <span className="truncate text-text-secondary">{lead.source?.name ?? '—'}</span>
      <span className="text-text-secondary">{formatRelativeDate(lead.lastContactedAt)}</span>
      <span className={isOverdueFollowUp ? 'flex items-center gap-1 font-semibold text-danger' : 'text-text-secondary'}>
        {isOverdueFollowUp && (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B42318" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3 2 20h20z" />
            <path d="M12 10v4M12 17h.01" />
          </svg>
        )}
        {formatRelativeDate(lead.nextFollowUpAt)}
      </span>
      <span className="flex justify-end">
        <Link to="/leads/$leadId" params={{ leadId: lead.id }} aria-label={`Open ${lead.fullName}`} className="flex size-6 items-center justify-center rounded-[6px] text-text-secondary hover:bg-surface-subtle">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <circle cx="5" cy="12" r="0.8" />
            <circle cx="12" cy="12" r="0.8" />
            <circle cx="19" cy="12" r="0.8" />
          </svg>
        </Link>
      </span>
    </div>
  );
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return `Today ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
